import { produce } from 'immer';

import {
  OmniElement,
  OmniStyleElement,
  html,
  css,
  ref,
  createRef,
  classMap,
  nothing,
  repeat,
} from 'omni-ui';

import { Om2Dropdown } from 'omni-campaign-ui';
import './persona-base-card.js';
import '../campaign-wysiwyg.js';

import { capitalizeWord } from '../../helpers/utils.js';

OmniStyleElement.register();
Om2Dropdown.register();

/**
 * @summary Class for PersonaEmpathyCard
 * @description Card for {@link CampaignAudiencePersona} that splits information into 4 distinct quadrants; Think, Feel, See, and Do.
 * @extends OmniElement
 */
export class PersonaEmpathyCard extends OmniElement {
  /**
   * @property {Object} empathyData - State passed down by parent component: {
      cardType: 'empathy-persona',
      editorPlaceholder: 'Enter quadrant description',
      title: 'Empathy Map',
      quadrants: {
        think: '',
        feel: '',
        see: '',
        do: '',
      },
    }
   * @property {Object} internalState - Internal state of the class before updating parent state
   * @property {Boolean} isEditView - tracks which view EmpathyPersona is on
   * @property {Boolean} readOnly - Boolean determining if content is readOnly or editable
   * @property {String} selectedQuadrant - the specific quadrant data is currently being added to or edited
   */
  static get properties() {
    return {
      empathyData: { type: Object },
      internalState: { type: Object },
      isEditView: { type: Boolean },
      readOnly: { type: Boolean },
      selectedQuadrant: { type: String },
    };
  }

  /**
   * Empathy Persona module styles
   */
  static get styles() {
    return [
      super.styles,
      css`
        :host,
        omni-style.omni,
        .omni .box persona-base-card {
          display: flex;
          flex: 1 1 auto;
          min-height: 36.57rem; /*force browsers to allow card to shrink and not overflow container*/
        }

        .display-view::part(base-card-container) {
          padding: 1.71rem 0 0 0;
        }

        ::part(title) {
          padding: 0 1.71rem;
        }

        .grid-container {
          height: 100%;
          display: grid;
          grid-template-columns: 1fr 1fr;
          grid-template-rows: 1fr 1fr;
          grid-template-areas:
            'viz-think viz-feel'
            'viz-see   viz-do';
          gap: 0.14rem;
          padding: 0.14rem;
          border-radius: 0 0 0.43rem 0.43rem;
          overflow: clip;
          flex: 1 1 auto;
          min-height: 0;
        }

        .viz {
          display: flex;
          flex-flow: column nowrap;
          gap: 1.077rem;
          padding: 1.077rem 1.71rem;
          background-color: rgba(var(--rgb-primary), 0.5);
          overflow: clip;
          flex: 1 1 auto;
          min-height: 0;
        }

        div.viz-label {
          display: flex;
          font-weight: bold;
          line-height: 1;
          color: rgba(var(--color-core-dark) 0.78);
          text-transform: capitalize;
        }

        div.viz-feel div.viz-label,
        div.viz-do div.viz-label {
          justify-content: flex-end;
        }

        .content-container {
          display: flex;
          overflow: clip;
          flex: 1 1 auto;
          min-height: 0;
        }

        div.viz.viz-see {
          border-radius: 0 0 0 0.71rem;
        }

        div.viz.viz-do {
          border-radius: 0 0 0.71rem 0;
        }

        .edit-view::part(base-card-container) {
          min-width: 30.286rem;
          height: 100%;
        }

        .omni div.field.input-container {
          margin-bottom: 2.14rem;
        }

        div.field > label.label {
          font-size: 0.79rem;
          color: var(--color-almost-black);
        }

        .omni div.field.text-container {
          display: flex;
          flex-flow: column nowrap;
          flex: 1 1 auto;
          min-height: 0;
          margin-bottom: 1.71rem;
        }

        div.control.text-area {
          height: 100%;
          min-height: 0;
        }
      `,
    ];
  }

  /**
   * Initializes {@link #internalState} {@link #isEditView}, {@link #readOnly}, {@link #selectedQuadrant} properties
   */
  constructor() {
    super();
    this.internalState = {};
    this.editWysiwyg = createRef();
    this.isEditView = false;
    this.readOnly = false;
    this.selectedQuadrant = 'think';
  }

  /**
   * Instantiates {@link #internalState} to {@link #empathyData}
   */
  connectedCallback() {
    super.connectedCallback();
    this.internalState = this.empathyData;
  }

  /**
   * @summary controls what view the card renders by reassigning {@link #isEditView} to the boolean passed. Always resets {@link #selectedQuadrant} to 'think'
   */
  _setView(isEdit) {
    this.isEditView = isEdit;
    this.selectedQuadrant = 'think';
  }

  /**
   *
   * @param {CampaignWYSIWYG#_handleContentChange~event:change} event - object containing event detail which is the value from the WYSIWYG editor content.
   * @summary Utilizes produce from immer to make a draft of the state {@link #internalState}, modifies the draft, and reassigns the state to correct quadrant.
   */
  _onEditorChange = e => {
    const { value } = e.detail;
    this.internalState = produce(this.internalState, draft => {
      draft.quadrants[this.selectedQuadrant] = value;
    });
  };

  /**
   * @summary  Updates {@link #empathyData} to {@link #internalState} and emits @event persona-change that {@link CampaignAudiencePersona#_updatePersonaData} listens to and updates the modules state with.
   */
  _updateCardData = () => {
    this.empathyData = { ...this.internalState };
    this._setView(false);
    this.dispatchNewEvent('persona-change', {
      detail: { cardData: this.empathyData },
    });
  };

  /**
   * @returns {TemplateResult} html to render display view
   */
  _renderDisplayView = () => {
    // maintains order quadrants need to display in
    const quadrantNames = ['think', 'feel', 'see', 'do'];
    return html`
      <div class="grid-container">
        ${repeat(
          quadrantNames,
          q => q,
          q => html`
            <div class="viz viz-${q}">
              <div class="viz-label">${q}</div>
              <div class="content-container">
                ${this.empathyData?.quadrants?.[q]
                  ? html`<campaign-wysiwyg
                      @change=${this._onEditorChange}
                      .initialContent=${this.empathyData.quadrants[q]}
                      display-only
                    ></campaign-wysiwyg>`
                  : nothing}
              </div>
            </div>
          `,
        )}
      </div>
    `;
  };

  /**
   * @returns {TemplateResult} html to render edit view
   */
  _renderEditView = () => {
    // maintains order quadrants need to display in
    const quadrantNames = ['think', 'feel', 'see', 'do'];
    return html`
      <div class="field input-container">
        <label class="label">Quadrant title</label>
        <div class="control">
          <om2-dropdown
            .options=${quadrantNames.map(q => ({
              label: capitalizeWord(q),
              value: q,
            }))}
            placeholder="Select quadrant"
            .value=${this.selectedQuadrant}
            @change=${({ detail: { value } }) => {
              const { value: wysiwyg } = this.editWysiwyg;

              this.selectedQuadrant = value;
              wysiwyg.setContent(this.internalState?.quadrants[value]);
            }}
          >
          </om2-dropdown>
        </div>
      </div>
      <div class="field text-container">
        <label class="label">Quadrant description</label>
        <div class="control text-area">
          <campaign-wysiwyg
            ${ref(this.editWysiwyg)}
            @change=${this._onEditorChange}
            placeholder=${this.internalState.editorPlaceholder}
            .initialContent=${this.internalState?.quadrants[
              this.selectedQuadrant
            ]}
          ></campaign-wysiwyg>
        </div>
      </div>
    `;
  };

  /**
   *
   * @returns {TemplateResult} main HTML to be rerender on the DOM
   */
  render() {
    return html`
      <omni-style>
        <persona-base-card
          class=${classMap({
            'edit-view': this.isEditView,
            'display-view': !this.isEditView,
          })}
          .isEditView=${this.isEditView}
          .readOnly=${this.readOnly}
          .cardTitle=${this.empathyData?.title}
          @persona-card-close=${() => {
            this._setView(false);
          }}
          @persona-card-submit=${this._updateCardData}
          @persona-card-edit-click=${() => {
            this._setView(true);
          }}
        >
          ${this.isEditView
            ? this._renderEditView()
            : this._renderDisplayView()}
        </persona-base-card>
      </omni-style>
    `;
  }
}

OmniElement.register('persona-empathy-card', PersonaEmpathyCard);
